import recruitmentData from './data.json'
import type {
  ComputedRecruitmentArea,
  PrefectureGrid,
  RecruitmentArea,
  RecruitmentProgress,
} from '../types'

export const PREFECTURE_GRID = recruitmentData.prefectureGrid as Record<string, PrefectureGrid>
export const AREA_DATA = recruitmentData.areaData as Record<string, RecruitmentArea[]>

const COLOR_STOPS = [
  { threshold: 0, color: [225, 23, 23] },
  { threshold: 0.38, color: [255, 180, 67] },
  { threshold: 0.68, color: [254, 203, 0] },
  { threshold: 1, color: [32, 160, 147] },
]

export const computeRecruitmentArea = (area: RecruitmentArea): ComputedRecruitmentArea => {
  const remaining = Math.max(0, area.req - area.cur)
  const percentage = area.req
    ? Math.min(100, Math.round((Math.min(area.cur, area.req) / area.req) * 100))
    : 100

  if (remaining === 0) {
    return { ...area, remaining, percentage, status: '達成済み', priority: 'C' }
  }
  if (percentage >= 80) {
    return { ...area, remaining, percentage, status: 'あと少し', priority: 'B' }
  }
  if (percentage >= 50) {
    return { ...area, remaining, percentage, status: '募集中', priority: 'A' }
  }
  if (percentage >= 25) {
    return { ...area, remaining, percentage, status: '募集中', priority: 'S' }
  }
  return { ...area, remaining, percentage, status: '大募集中', priority: 'SS' }
}

export const getRecruitmentProgress = (
  areasByPrefecture: Record<string, RecruitmentArea[]>
): RecruitmentProgress => {
  let goal = 0
  let current = 0

  Object.values(areasByPrefecture).forEach((areas) => {
    areas.forEach((area) => {
      goal += area.req
      current += Math.min(area.cur, area.req)
    })
  })

  return {
    goal,
    current,
    remaining: Math.max(0, goal - current),
    percentage: goal ? Math.round((current / goal) * 100) : 0,
  }
}

export const getPrefecturePercentage = (areas: RecruitmentArea[]): number => {
  const progress = getRecruitmentProgress({ prefecture: areas })
  return progress.percentage
}

export const getPrefectureColor = (areas?: RecruitmentArea[]): string => {
  if (!areas?.length) return '#e4e4e4'

  const ratio = getPrefecturePercentage(areas) / 100
  for (let index = 0; index < COLOR_STOPS.length - 1; index += 1) {
    const from = COLOR_STOPS[index]
    const to = COLOR_STOPS[index + 1]
    if (ratio <= to.threshold) {
      const progress = (ratio - from.threshold) / (to.threshold - from.threshold)
      const color = from.color.map((value, channel) =>
        Math.round(value + (to.color[channel] - value) * progress)
      )
      return `rgb(${color[0]}, ${color[1]}, ${color[2]})`
    }
  }

  return 'rgb(32, 160, 147)'
}

export const getContrastingTextColor = (color: string): '#333333' | '#ffffff' => {
  const values = color.match(/\d+/g)?.map(Number)
  if (!values || values.length < 3) return '#333333'

  const channel = (value: number) => {
    const normalized = value / 255
    return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4)
  }
  const backgroundLuminance =
    0.2126 * channel(values[0]) + 0.7152 * channel(values[1]) + 0.0722 * channel(values[2])
  const inkLuminance = channel(0x33)
  const whiteContrast = 1.05 / (backgroundLuminance + 0.05)
  const inkContrast = (backgroundLuminance + 0.05) / (inkLuminance + 0.05)

  return inkContrast > whiteContrast ? '#333333' : '#ffffff'
}
